import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setIgnoreUpdateSelection,
  setSelectedCharacterStyles,
  setSelectedParagraphs,
} from "src/features/review-slice";
import {
  selectCharacterInfo,
  selectFilteredCharacterInfo,
  selectFilteredParagraphInfo,
  selectParagraphInfo,
} from "src/features/selectors";
import {
  CHAR_STYLE_CC_TAG,
  saveReviewProgress,
  selectParagraphInDocument,
  selectTextInDocument,
} from "src/services";
import { store } from "src/store";
import { withoutSelectionHandling } from "src/taskpane/handlers";

import type { ReviewState } from "src/features/review-slice";
import type { RootState, AppDispatch } from "src/store";

interface SetInitialSelectionHookOptions {
  outlineMode: ReviewState["outlineMode"];
}

async function setInitialSelection(outlineMode: ReviewState["outlineMode"]) {
  if (outlineMode === "chapter" || outlineMode === "style") {
    const selection = await Word.run(async (context) => {
      const range = context.document.getSelection();
      const paragraphs = range.paragraphs;
      paragraphs.load("items");

      await context.sync();

      return paragraphs.items.map((p) => p.uniqueLocalId);
    });

    store.dispatch(setSelectedParagraphs(selection));
  }

  if (outlineMode === "character") {
    const selection = await Word.run(async (context) => {
      const range = context.document.getSelection();
      range.load();

      await context.sync();

      const rangeStart = range.start;
      const paragraph = range.paragraphs.getFirst();
      paragraph.load();
      const controls = paragraph.contentControls.getByTag(CHAR_STYLE_CC_TAG);
      controls.load();

      await context.sync();

      for (const cc of controls.items) {
        const ccRange = cc.getRange();
        ccRange.load();

        await context.sync();

        if (ccRange.start === rangeStart - 1) {
          cc.load();
          await context.sync();
          return cc.title;
        }
      }

      return null;
    });

    if (selection) {
      store.dispatch(setSelectedCharacterStyles(selection));
    }
  }
}

export function useSetInitialSelection({
  hasRestoredProgress,
  outlineMode,
}: SetInitialSelectionHookOptions & { hasRestoredProgress: React.RefObject<boolean> }) {
  React.useEffect(() => {
    setInitialSelection(outlineMode);
  }, [hasRestoredProgress.current]);
}

interface UpdateSelectionHookOptions {
  boundaryIndices: ReviewState["boundaryIndices"];
  filteredCharacterInfo: ReturnType<typeof selectFilteredCharacterInfo>;
  filteredParagraphInfo: ReturnType<typeof selectFilteredParagraphInfo>;
  ignoreParagraphs: ReviewState["ignoreParagraphs"];
  outlineMode: ReviewState["outlineMode"];
  selectedCharacterOriginFilter: ReviewState["selectedCharacterOriginFilter"];
  selectedCharacterStyleFilter: ReviewState["selectedCharacterStyleFilter"];
  selectedCharacterStyles: ReviewState["selectedCharacterStyles"];
  selectedGroupFilter: ReviewState["selectedGroupFilter"];
  selectedParagraphs: ReviewState["selectedParagraphs"];
  selectedStyleFilter: ReviewState["selectedStyleFilter"];
}

type CharacterInfo = UpdateSelectionHookOptions["filteredCharacterInfo"][number];
type ParagraphInfo = UpdateSelectionHookOptions["filteredParagraphInfo"][number];

// [paragraph index, character offset within the paragraph]
type DocumentPosition = [number, number];

function isAtOrAfter(a: DocumentPosition, b: DocumentPosition) {
  return a[0] > b[0] || (a[0] === b[0] && a[1] >= b[1]);
}

function getParagraphIndexById() {
  return new Map(selectParagraphInfo(store.getState()).map((p) => [p.uniqueLocalId, p.index]));
}

async function getWordCursorPosition(
  paragraphIndexById: Map<string, number>
): Promise<DocumentPosition | null> {
  const { paragraphId, offset } = await Word.run(async (context) => {
    const range = context.document.getSelection();
    const paragraph = range.paragraphs.getFirst();
    const prefix = paragraph
      .getRange(Word.RangeLocation.start)
      .expandTo(range.getRange(Word.RangeLocation.start));
    paragraph.load("uniqueLocalId");
    prefix.load("text");
    await context.sync();
    return { paragraphId: paragraph.uniqueLocalId, offset: prefix.text.length };
  });
  const paragraphIndex = paragraphIndexById.get(paragraphId);
  return paragraphIndex === undefined ? null : [paragraphIndex, offset];
}

// Span positions bracketing the cursor: Next searches from `end`, Previous from `start`.
async function getCharacterCursorPosition(
  paragraphIndexById: Map<string, number>,
  selectedCharacterStyles: string | null
): Promise<{ start: DocumentPosition; end: DocumentPosition } | null> {
  const selected = selectCharacterInfo(store.getState()).find(
    (c) => c.id === selectedCharacterStyles
  );
  if (selected) {
    const paragraphIndex = paragraphIndexById.get(selected.paragraphUniqueLocalId);
    if (paragraphIndex === undefined) return null;
    return {
      start: [paragraphIndex, selected.rangeStart],
      end: [paragraphIndex, selected.rangeEnd],
    };
  }

  const cursor = await getWordCursorPosition(paragraphIndexById);
  return cursor && { start: cursor, end: cursor };
}

export async function getCharacterInfoFromCursor(
  filteredCharacterInfo: CharacterInfo[],
  selectedCharacterStyles: string | null,
  direction: "next" | "previous",
  { fallBackToFirst = false } = {}
): Promise<CharacterInfo | null> {
  if (filteredCharacterInfo.length === 0) return null;

  const paragraphIndexById = getParagraphIndexById();
  const byPosition = filteredCharacterInfo
    .map((c) => ({
      info: c,
      position: [
        paragraphIndexById.get(c.paragraphUniqueLocalId) ?? Infinity,
        c.rangeStart,
      ] as DocumentPosition,
    }))
    .sort((a, b) => a.position[0] - b.position[0] || a.position[1] - b.position[1]);

  const cursor = await getCharacterCursorPosition(paragraphIndexById, selectedCharacterStyles);
  if (!cursor) return byPosition[0].info;

  const fallback = fallBackToFirst ? byPosition[0].info : null;
  if (direction === "next") {
    return byPosition.find((c) => isAtOrAfter(c.position, cursor.end))?.info ?? fallback;
  }
  const before = byPosition.filter((c) => !isAtOrAfter(c.position, cursor.start));
  return before[before.length - 1]?.info ?? fallback;
}

function getNextParagraphInfoFromCursor(
  filteredParagraphInfo: ParagraphInfo[],
  selectedParagraphs: string[] | null
): ParagraphInfo {
  const paragraphIndexById = getParagraphIndexById();
  const cursorIndices = (selectedParagraphs ?? []).flatMap(
    (id) => paragraphIndexById.get(id) ?? []
  );
  if (cursorIndices.length === 0) return filteredParagraphInfo[0];

  const cursorIndex = Math.max(...cursorIndices);
  return filteredParagraphInfo.find((p) => p.index > cursorIndex) ?? filteredParagraphInfo[0];
}

async function updateSelection({
  filteredCharacterInfo,
  filteredParagraphInfo,
  outlineMode,
  selectedCharacterStyles,
  selectedParagraphs,
}: Pick<
  UpdateSelectionHookOptions,
  | "filteredCharacterInfo"
  | "filteredParagraphInfo"
  | "outlineMode"
  | "selectedCharacterStyles"
  | "selectedParagraphs"
>) {
  try {
    const { ignoreUpdateSelection } = store.getState().review;

    if (ignoreUpdateSelection) {
      store.dispatch(setIgnoreUpdateSelection(false));
      return;
    }

    if (outlineMode === "character") {
      const isSelected = filteredCharacterInfo.some((i) => i.id === selectedCharacterStyles);
      if (!isSelected && filteredCharacterInfo.length) {
        const target = await getCharacterInfoFromCursor(
          filteredCharacterInfo,
          selectedCharacterStyles,
          "next",
          { fallBackToFirst: true }
        );
        if (!target) return;
        const { id } = target;
        store.dispatch(setSelectedCharacterStyles(id));
        await withoutSelectionHandling(() => selectTextInDocument(id));
        await saveReviewProgress({ selectedCharacterStyleId: id });
        return;
      }
    }

    if (outlineMode === "chapter" || outlineMode === "style") {
      const isSelected = filteredParagraphInfo.some((i) =>
        selectedParagraphs?.includes(i.uniqueLocalId)
      );
      if (!isSelected && filteredParagraphInfo.length) {
        // Chapter mode starts at the top of the chapter; style mode continues from the cursor.
        const { uniqueLocalId } =
          outlineMode === "style"
            ? getNextParagraphInfoFromCursor(filteredParagraphInfo, selectedParagraphs)
            : filteredParagraphInfo[0];
        store.dispatch(setSelectedParagraphs([uniqueLocalId]));
        await withoutSelectionHandling(() => selectParagraphInDocument(uniqueLocalId));
      }
    }
  } catch (e) {
    console.error(e);
  }
}

export function useUpdateSelection({
  boundaryIndices,
  filteredCharacterInfo,
  filteredParagraphInfo,
  ignoreParagraphs,
  outlineMode,
  selectedCharacterOriginFilter,
  selectedCharacterStyleFilter,
  selectedCharacterStyles,
  selectedGroupFilter,
  selectedParagraphs,
  selectedStyleFilter,
}: UpdateSelectionHookOptions) {
  React.useEffect(() => {
    updateSelection({
      filteredCharacterInfo,
      filteredParagraphInfo,
      outlineMode,
      selectedCharacterStyles,
      selectedParagraphs,
    });
  }, [
    boundaryIndices,
    ignoreParagraphs,
    selectedCharacterOriginFilter,
    selectedCharacterStyleFilter,
    selectedGroupFilter,
    selectedStyleFilter,
  ]);
}

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
